import React from 'react'
import { Button, Col, Row } from 'react-bootstrap'
import { Grade, PermissionReturn, Staff } from '../../../../back-end/utilities/apiDefinitions'
import { AppContext } from '../../App'
import * as Request from '../../utilities/request'
import { PageStatus } from '../../constants/pageStatus'
import { Loading } from '../ui/Loading/Loading'

import './staffSettings.css'
import { formatDateExcludeEpoch, lastNameFromFull } from '../../utilities/format'
import { FormText } from '../ui/Form/Text'
import EditStaffModal from './editStaffModal'
import { OnboardingStep } from '../../screens/onBoarding/onBoarding'
import { InvoiceFilterBadge } from '../invoice/invoiceFilterBadge'
import { DateTime } from 'luxon'
import { useStateParams } from '../../utilities/hooks'
import OffBoardStaffModal from './offboardStaffModal'

const staffStatus = (lastDay: string | null) => {
	if (lastDay) {
		return DateTime.fromISO(lastDay).startOf('day') > DateTime.now().startOf('day') ? 'current' : 'past'
	}
	return 'current'
}

import { MessageAction } from '../ui/messages/message'

interface BillyStaffSettingsCardProps {
	updateMessages: (action: MessageAction) => void
}

const StaffSettings = (props: BillyStaffSettingsCardProps) => {
	const context = React.useContext(AppContext)
	const [pageStatus, setPageStatus] = React.useState<PageStatus>('Loading')

	const [allStaff, setAllStaff] = React.useState<Staff[]>([])
	const [permissions, setPermissions] = React.useState<PermissionReturn[]>([])
	const [grades, setGrades] = React.useState<Grade[]>([])
	const [staffSearch, setStaffSearch] = React.useState('')
	const [modalStaff, setModalStaff] = React.useState<Staff | null>(null)
	const [offboardStaff, setOffboardStaff] = React.useState<Staff | null>(null)
	const [statusFilter, setStatusFilter] = useStateParams<string[]>(
		['current'],
		'status',
		(d) => JSON.stringify(d),
		(d) => JSON.parse(d)
	)

	React.useEffect(() => {
		const getData = async () => {
			const [staffRes, permissionRes, gradeRes] = await Promise.all([
				Request.get('staff'),
				Request.get('permission?where=PermissionIsBilly==1'),
				Request.get('grade'),
			])

			setAllStaff(staffRes.data.staff)
			setPermissions(permissionRes.data)
			setGrades(gradeRes.data.grades)

			setPageStatus('Ready')
		}

		setPageStatus('Loading')
		if (context.appState.authState.isLoggedIn) {
			getData()
		}
	}, [context.appState])

	React.useEffect(() => {
		if (context.appState.onBoardingStep === OnboardingStep.StaffSettings) {
			props.updateMessages({
				type: 'add',
				data: {
					severity: 'info',
					message:
						'Finish your onboarding by updating the settings for your staff.\nIf not all of your staff are listed, you may need to wait for the initial sync to complete.',
					timeout: 0,
					dismissible: true,
				},
			})

			context.updateAppState({
				state: 'onboarding',
				data: {
					...context.appState,
					onBoardingStep: null,
				},
			})
		}
	}, [context.appState.onBoardingStep])
	const handleFilterClick = (filter: string) =>
		setStatusFilter(statusFilter.includes(filter) ? statusFilter.filter((s) => s !== filter) : statusFilter.concat([filter]))

	const filteredStaff = React.useMemo(() => allStaff?.filter((s) => statusFilter.includes(staffStatus(s.terminationDate))) || [], [allStaff, statusFilter])

	if (pageStatus === 'Loading') {
		return (
			<div className="center-flex p-5">
				<Loading show />
			</div>
		)
	}

	const colWidths = [2, 2, 2, 2, 2, 1, 1]

	return (
		<>
			<Row className="align-items-center pb-2">
				<Col sm="auto">
					<h3 className="mb-0">Staff Members</h3>
				</Col>
				<Col>
					<InvoiceFilterBadge
						key="current"
						text="Current"
						amount={
							(statusFilter.includes('current') ? filteredStaff : allStaff)
								?.filter((staff) => staff.name.toLowerCase().includes(staffSearch.toLowerCase()))
								.filter((s) => !s.terminationDate || DateTime.fromISO(s.terminationDate).startOf('day') > DateTime.now().startOf('day'))
								.length || 0
						}
						handleClick={handleFilterClick}
						selected={statusFilter.includes('current')}
					/>
				</Col>
				<Col>
					<InvoiceFilterBadge
						key="past"
						text="Past"
						amount={
							(statusFilter.includes('past') ? filteredStaff : allStaff)
								?.filter((staff) => staff.name.toLowerCase().includes(staffSearch.toLowerCase()))
								.filter((s) => s.terminationDate && DateTime.fromISO(s.terminationDate).startOf('day') <= DateTime.now().startOf('day'))
								.length || 0
						}
						handleClick={handleFilterClick}
						selected={statusFilter.includes('past')}
					/>
				</Col>

				<Col sm="auto" className="pr-0">
					<span>Search Name:</span>
				</Col>
				{/* <Col sm="auto" className="pr-0">
					<button
						onClick={() => {
							console.log(allStaff, permissions)
						}}
					>
						log
					</button>
				</Col> */}
				<Col sm="auto" className="pl-1">
					<FormText name="staffSearch" value={staffSearch} onChange={(e) => setStaffSearch(e.target.value)} />
				</Col>
				<Col />
			</Row>
			<Row className="staff-list-header align-items-center py-3">
				<Col sm={colWidths[0]}>Name</Col>
				<Col sm={colWidths[1]}>Permission</Col>
				<Col sm={colWidths[2]}>Grade</Col>
				<Col sm={colWidths[3]}>Grade Effective Date</Col>
				<Col sm={colWidths[4]}>Reporting Group</Col>
				<Col sm={colWidths[5]}>Std Hours</Col>
				<Col sm={colWidths[6]}></Col>
			</Row>
			{filteredStaff
				.filter((staff) => staff.name.toLowerCase().includes(staffSearch.toLowerCase()))
				.sort((a, b) => (lastNameFromFull(a.name) < lastNameFromFull(b.name) ? -1 : 1))
				.map((staff) => {
					const newestStaffGrade = staff.staffGradeHistory?.sort((a, b) => (a.effectiveDate > b.effectiveDate ? -1 : 1)).at(0) || null
					const newestStandardWorkingHours =
						staff.standardWorkingHoursHistory?.sort((a, b) => (a.effectiveDate > b.effectiveDate ? -1 : 1)).at(0) || null
					return (
						<Row key={staff.staffID} className="align-items-center pt-3">
							<Col sm={colWidths[0]}>{staff.name}</Col>
							<Col sm={colWidths[1]}>{permissions.find((p) => p.id === staff.permissionID)?.name || '-'}</Col>
							<Col sm={colWidths[2]}>{newestStaffGrade?.grade?.name || '-'}</Col>
							<Col sm={colWidths[3]}>{newestStaffGrade?.effectiveDate ? formatDateExcludeEpoch(newestStaffGrade.effectiveDate) : '-'}</Col>
							<Col sm={colWidths[4]}>{allStaff.find((s) => s.xpmID === staff.reportGroupStaffXpmID)?.name || '-'}</Col>
							<Col sm={colWidths[5]}>{newestStandardWorkingHours?.hours || '-'}</Col>
							<Col sm={colWidths[6]}>
								<Button variant="secondary" className="px-1 py-2" onClick={() => setModalStaff(staff)}>
									Update
								</Button>
							</Col>
						</Row>
					)
				})}

			{modalStaff !== null && (
				<>
					<EditStaffModal
						authState={context.appState.authState}
						modalStaff={modalStaff}
						setModalStaff={setModalStaff}
						allStaff={allStaff}
						setAllStaff={setAllStaff}
						permissions={permissions}
						grades={grades}
						setOffboardStaff={setOffboardStaff}
					/>
				</>
			)}
			{offboardStaff !== null && (
				<>
					<OffBoardStaffModal
						authState={context.appState.authState}
						offboardStaff={offboardStaff}
						setOffboardStaff={setOffboardStaff}
						allStaff={allStaff}
						setAllStaff={setAllStaff}
					/>
				</>
			)}
		</>
	)
}

export default StaffSettings
